import { useState, useEffect, useRef } from 'react';

interface RecordingScreenProps {
  onComplete: (audioBlob: Blob, durationSeconds: number) => void;
  disabled?: boolean;
  disabledMessage?: string | null;
}

function pickMimeType(): string | undefined {
  const candidates = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4',
  ];
  return candidates.find((candidate) => MediaRecorder.isTypeSupported(candidate));
}

export default function RecordingScreen({ onComplete, disabled = false, disabledMessage = null }: RecordingScreenProps) {
  const [isRecording, setIsRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [waveHeights, setWaveHeights] = useState<number[]>(Array.from({ length: 36 }, () => 12));
  const [error, setError] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const waveRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const secondsRef = useRef(0);

  useEffect(() => {
    if (isRecording) {
      timerRef.current = setInterval(() => {
        setSeconds((s) => {
          secondsRef.current = s + 1;
          return s + 1;
        });
      }, 1000);
      waveRef.current = setInterval(() => {
        setWaveHeights(Array.from({ length: 36 }, () => Math.floor(Math.random() * 52) + 8));
      }, 120);
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
      if (waveRef.current) clearInterval(waveRef.current);
      setWaveHeights(Array.from({ length: 36 }, () => 12));
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (waveRef.current) clearInterval(waveRef.current);
    };
  }, [isRecording]);

  const formatTime = (s: number) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

  const stopStream = () => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  };

  useEffect(() => stopStream, []);

  const startRecording = async () => {
    setError(null);
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      setError('Браузер не поддерживает запись звука. Попробуйте Chrome или Safari новой версии.');
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = pickMimeType();
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);

      streamRef.current = stream;
      recorderRef.current = recorder;
      chunksRef.current = [];
      secondsRef.current = 0;
      setSeconds(0);

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType || 'audio/webm' });
        stopStream();
        setTimeout(() => onComplete(blob, secondsRef.current), 600);
      };

      recorder.start();
      setIsRecording(true);
    } catch {
      stopStream();
      setError('Не получилось получить доступ к микрофону. Проверьте разрешения браузера.');
    }
  };

  const stopRecording = () => {
    recorderRef.current?.stop();
    setIsRecording(false);
  };

  const handleToggle = () => {
    if (disabled) return;
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  return (
    <div
      className="flex flex-col w-full h-screen"
      style={{
        background: 'linear-gradient(170deg, #1A1B2E 0%, #2D1B3D 60%, #1F1A35 100%)',
        overflowY: 'auto',
        overflowX: 'hidden',
        WebkitOverflowScrolling: 'touch',
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-5 pt-12 pb-4">
        <button style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
          <div className="w-8 h-8 flex items-center justify-center rounded-full" style={{ background: 'rgba(255,255,255,0.08)' }}>
            <i className="ri-arrow-left-s-line text-xl" style={{ color: '#A8B2C8' }} />
          </div>
        </button>
        <span style={{ fontSize: '17px', fontWeight: 600, color: '#F5F5F7' }}>Первое воспоминание</span>
        <div style={{ width: '32px' }} />
      </div>

      {/* Helper text */}
      <div className="px-8 pb-6">
        <p style={{ fontSize: '15px', color: '#A8B2C8', lineHeight: 1.7, textAlign: 'center' }}>
          Нажмите на кнопку и в свободной форме расскажите о самом запоминающемся событии последних дней.
        </p>
      </div>

      {/* Main recording area */}
      <div className="flex-1 flex flex-col items-center justify-center gap-8 pb-6">
        {/* Timer */}
        <div style={{ height: '28px', display: 'flex', alignItems: 'center' }}>
          {isRecording && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div
                style={{
                  width: '8px', height: '8px', borderRadius: '50%', background: '#FF6B6B',
                  animation: 'blink 1s ease-in-out infinite',
                }}
              />
              <span style={{ fontSize: '18px', fontWeight: 600, color: '#F5F5F7', fontVariantNumeric: 'tabular-nums' }}>{formatTime(seconds)}</span>
            </div>
          )}
        </div>

        {/* Record button */}
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {/* Outer glow rings (idle only) */}
          {!isRecording && (
            <>
              <div
                style={{
                  position: 'absolute',
                  width: '160px', height: '160px',
                  borderRadius: '50%',
                  border: '2px solid rgba(62,207,160,0.2)',
                  pointerEvents: 'none',
                }}
              />
              <div
                style={{
                  position: 'absolute',
                  width: '190px', height: '190px',
                  borderRadius: '50%',
                  border: '1px solid rgba(62,207,160,0.1)',
                  pointerEvents: 'none',
                }}
              />
            </>
          )}

          <button
            onClick={handleToggle}
            disabled={disabled}
            className="transition-all duration-400 active:scale-95"
            style={{
              width: '124px',
              height: '124px',
              borderRadius: isRecording ? '28px' : '50%',
              background: isRecording
                ? 'linear-gradient(135deg, #FF6B6B, #EE5A52)'
                : 'linear-gradient(135deg, #3ECFA0, #2BA87F)',
              border: 'none',
              cursor: disabled ? 'default' : 'pointer',
              opacity: disabled ? 0.55 : 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: isRecording
                ? '0 8px 32px rgba(255,107,107,0.45)'
                : '0 8px 32px rgba(62,207,160,0.45)',
              transition: 'all 0.35s cubic-bezier(0.34, 1.56, 0.64, 1)',
            }}
          >
            {isRecording
              ? <div style={{ width: '36px', height: '36px', borderRadius: '6px', background: '#fff' }} />
              : <i className="ri-mic-fill text-5xl text-white" />
            }
          </button>
        </div>

        {/* Label */}
        <p style={{
          fontSize: '16px', fontWeight: 500,
          color: isRecording ? '#FF6B6B' : '#F5F5F7',
          transition: 'color 0.3s ease',
        }}>
          {isRecording ? 'Запись идёт...' : disabled ? 'Подключаем Омут Памяти' : 'Начать запись'}
        </p>

        {(error || disabledMessage) && (
          <p style={{ fontSize: '13px', color: error ? '#FFB4B4' : '#6B7A94', textAlign: 'center', lineHeight: 1.5, maxWidth: '280px' }}>
            {error || disabledMessage}
          </p>
        )}

        {/* Sound wave */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '3px',
            height: '72px',
            opacity: isRecording ? 1 : 0,
            transition: 'opacity 0.4s ease',
          }}
        >
          {waveHeights.map((h, i) => (
            <div
              key={i}
              style={{
                width: '3px',
                height: `${h}px`,
                borderRadius: '2px',
                background: 'linear-gradient(to top, transparent, #3ECFA0)',
                transition: isRecording ? 'height 0.12s ease' : 'none',
              }}
            />
          ))}
        </div>
      </div>

      {/* Bottom note */}
      <div
        className="px-8"
        style={{ paddingBottom: 'calc(32px + env(safe-area-inset-bottom))' }}
      >
        <p style={{ fontSize: '13px', color: '#6B7A94', textAlign: 'center', lineHeight: 1.5 }}>
          {isRecording ? 'Нажмите ещё раз, чтобы остановить запись' : 'Говорите свободно — никто, кроме вас, не услышит'}
        </p>
      </div>

      <style>{`@keyframes blink { 0%,100%{opacity:1} 50%{opacity:0.2} }`}</style>
    </div>
  );
}
